import { CloseRounded } from "@mui/icons-material";
import classNames from "classnames";
import {
  AnimatePresence,
  PanInfo,
  motion,
  useDragControls,
} from "framer-motion";
import { ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useBackToClose } from "../../hooks/useBackToClose";
import { useIsMobile } from "../../hooks/useMediaQuery";
import IconButton from "./IconButton";
import styles from "./css/Modal.module.css";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  // auto: 모바일은 바텀시트, 태블릿 이상은 가운데 다이얼로그
  placement?: "auto" | "sheet" | "center";
  size?: "sm" | "md" | "lg";
  // false 면 바깥 클릭 · ESC · 닫기 버튼으로 닫을 수 없다 (필수 확인 다이얼로그)
  dismissible?: boolean;
  showCloseButton?: boolean;
  closeLabel?: string;
}

let openModalCount = 0;

function lockBodyScroll() {
  openModalCount += 1;
  if (openModalCount === 1) {
    document.body.style.overflow = "hidden";
  }
}

function unlockBodyScroll() {
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) {
    document.body.style.overflow = "";
  }
}

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  placement = "auto",
  size = "md",
  dismissible = true,
  showCloseButton = false,
  closeLabel = "닫기",
}: ModalProps) {
  const isMobile = useIsMobile();
  const isSheet = placement === "sheet" || (placement === "auto" && isMobile);
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useBackToClose(open && dismissible, onClose);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    lockBodyScroll();
    panelRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && dismissible) {
        event.stopPropagation();
        onCloseRef.current();
      }
    }
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      unlockBodyScroll();
      previouslyFocused?.focus?.();
    };
  }, [open, dismissible]);

  function handleDragEnd(_: unknown, info: PanInfo) {
    if (dismissible && (info.offset.y > 120 || info.velocity.y > 600)) {
      onClose();
    }
  }

  const panelMotion = isSheet
    ? {
        initial: { y: "100%" },
        animate: { y: 0 },
        exit: { y: "100%" },
        transition: { type: "spring" as const, damping: 32, stiffness: 380 },
      }
    : {
        initial: { opacity: 0, scale: 0.96 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.96 },
        transition: { duration: 0.18, ease: [0.2, 0, 0, 1] },
      };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className={classNames(styles.root, {
            [styles.rootSheet]: isSheet,
          })}
        >
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => dismissible && onClose()}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={description ? descriptionId : undefined}
            tabIndex={-1}
            className={classNames(styles.panel, styles[size], {
              [styles.sheet]: isSheet,
              [styles.center]: !isSheet,
            })}
            drag={isSheet && dismissible ? "y" : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={handleDragEnd}
            {...panelMotion}
          >
            <div
              className={classNames({ [styles.dragArea]: isSheet })}
              onPointerDown={(e) => isSheet && dragControls.start(e)}
            >
              {isSheet && <div className={styles.handle} aria-hidden />}
              {(title || (showCloseButton && dismissible)) && (
                <div className={styles.header}>
                  {title && (
                    <h2 id={titleId} className={styles.title}>
                      {title}
                    </h2>
                  )}
                  {showCloseButton && dismissible && (
                    <IconButton
                      aria-label={closeLabel}
                      icon={<CloseRounded />}
                      variant="ghost"
                      size="sm"
                      className={styles.close}
                      onClick={onClose}
                    />
                  )}
                </div>
              )}
            </div>
            {description && (
              <p id={descriptionId} className={styles.description}>
                {description}
              </p>
            )}
            {children && <div className={styles.body}>{children}</div>}
            {footer && <div className={styles.footer}>{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
